import { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { TeamStrengthEditor } from "./TeamStrengthEditor";
import { LeagueSeasonComparison } from "./LeagueSeasonComparison";
import {
  CompactLeagueTable,
  prepareCompactLeagueRows,
} from "./CompactLeagueTable";
import { leagueComparisonRows } from "../helpers/leagueComparisonRows";
import { fetchViaPost } from "../helpers/fetchViaPost";
import { sortAndRankTeams } from "../helpers/sortAndRankTeams";
import { selectPreviousLeagueSeason } from "../helpers/previousLeagueSeason";
import "./LeagueWorkarea.css";

export const settingNumber = (value) =>
  value === null ||
  value === undefined ||
  value === "" ||
  !Number.isFinite(Number(value))
    ? null
    : Number(value);
const fmt = (value, digits = 3) =>
  settingNumber(value) === null
    ? "–"
    : Number(value).toLocaleString("de-DE", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });
const pct = (value) =>
  settingNumber(value) === null ? "–" : `${fmt(value * 100, 1)} %`;

export function LeagueSection({
  title,
  summary,
  children,
  expanded,
  onChange,
  defaultExpanded = false,
}) {
  return (
    <Accordion
      className="league-work-section"
      disableGutters
      elevation={0}
      defaultExpanded={defaultExpanded}
      expanded={expanded}
      onChange={onChange}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box>
          <Typography
            component="h2"
            sx={{ fontWeight: 800, fontSize: "1.05rem" }}
          >
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {summary}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
}

export function leagueParameterMetrics(data) {
  return leagueComparisonRows(data).map((row) => ({
    ...row,
    goalAverage:
      row.fixtures > 0 ? (row.homeGoals + row.awayGoals) / row.fixtures : null,
    homeAdvantage:
      row.homeGoals + row.awayGoals > 0
        ? (2 * row.homeGoals) / (row.homeGoals + row.awayGoals)
        : null,
    drawRate: row.fixtures > 0 ? row.draws / row.fixtures : null,
  }));
}

export function parameterObservation(
  label,
  model,
  actual,
  expected,
  should,
  count
) {
  if (!(count > 0) || settingNumber(actual) === null)
    return `${label}: Noch keine auswertbaren Spiele vorhanden.`;
  if (settingNumber(model) === null)
    return `${label}: Modellwert fehlt; ${count} Spiele stehen zur Einordnung bereit.`;
  const describe = (value) =>
    settingNumber(value) === null
      ? "nicht verfügbar"
      : Math.abs(value - model) <= Math.max(Math.abs(model) * 0.03, 0.015)
      ? "nahe am Modell"
      : value > model
      ? "über dem Modell"
      : "unter dem Modell";
  return `${label}: Real liegt ${describe(actual)}. Expected: ${describe(
    expected
  )}; Should: ${describe(should)}. Basis Real: ${count} Spiele.`;
}

export function LeagueWorkarea({
  user,
  league,
  season,
  seasons,
  teams = [],
  comparison,
  base,
  onSaved,
  onDirtyChange,
  indications,
  diagnosis,
  teamLogos,
  renderWorkspaceContent,
}) {
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [notice, setNotice] = useState("");
  const apiRef = useRef();
  const [parametersOpen, setParametersOpen] = useState(true);
  const [teamsOpen, setTeamsOpen] = useState(false);
  const [strengthView, setStrengthView] = useState("should");
  const [goalsView, setGoalsView] = useState("should");
  const [previous, setPrevious] = useState(null);
  const [previousSummary, setPreviousSummary] = useState(null);
  const [previousError, setPreviousError] = useState("");
  const editorTeams = useMemo(
    () =>
      teams.map((team) => ({
        ...team,
        custom_name:
          team.custom_name && team.custom_name !== "NO_CUSTOM_NAME"
            ? team.custom_name
            : team.team_name,
      })),
    [teams]
  );
  const previousSeason = selectPreviousLeagueSeason(
    { ...season, league_id: league?.id },
    seasons
  );
  useEffect(() => {
    onDirtyChange?.(dirty);
    if (!dirty) return;
    const guard = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [dirty, onDirtyChange]);
  useEffect(() => {
    if (!renderWorkspaceContent && strengthView !== "previous" && goalsView !== "previous") return;
    if (!previousSeason?.id) return;
    let cancelled = false;
    setPreviousError("");
    fetchViaPost(
      "get-season-base-stats",
      JSON.stringify({
        league_id: league.id,
        season_id: previousSeason.id,
        fast_read: true,
      })
    )
      .then(async (response) => {
        if (!response.ok)
          throw new Error("Vorsaison konnte nicht geladen werden.");
        return response.json();
      })
      .then((result) => {
        if (!cancelled) { setPrevious(result.record?.actual_table || []); setPreviousSummary(result.record?.league_summary || null); }
      })
      .catch((error) => {
        if (!cancelled) setPreviousError(error.message);
      });
    return () => {
      cancelled = true;
    };
  }, [strengthView, goalsView, previousSeason?.id, league.id, renderWorkspaceContent]);
  const data = {
    ...comparison,
    season_base_summary:
      comparison?.season_base_summary || base?.league_summary,
  };
  const metrics = leagueParameterMetrics(data);
  const actual = metrics.find((row) => row.key === "actual");
  const expected = metrics.find((row) => row.key === "expected");
  const should = metrics.find((row) => row.key === "should");
  const marketRows =
    comparison?.record?.market_table || comparison?.record?.market?.table || [];
  const sources = {
    actual: base?.actual_table || [],
    expected: comparison?.record?.expected_table || [],
    should: comparison?.record?.should_table || [],
    market: marketRows,
    previous: previous || [],
  };
  const comparisons = Object.fromEntries(
    Object.entries(sources).map(([key, values]) => [
      key,
      new Map(
        prepareCompactLeagueRows(
          values,
          key === "previous" ? "actual" : key
        ).map((row) => [row.teamId, row])
      ),
    ])
  );
  const options = [
    ["expected", "Expected bisher"],
    ["actual", "Real / erspielte Leistung"],
    ["should", "Should"],
    ...(previousSeason
      ? [["previous", `Vorsaison ${previousSeason.name || ""} · Real`]]
      : []),
    ...(marketRows.length ? [["market", "Market Derived"]] : []),
  ];
  const comparisonCell = (team, view, field) => {
    const entry = comparisons[view]?.get(Number(team.team_id));
    const value = settingNumber(entry?.fixtureSettingValues?.[field]);
    const setting = settingNumber(team[field]);
    const delta = value !== null && setting !== null ? setting - value : null;
    return (
      <>
        <b>{fmt(value)}</b>
        <small>
          {delta !== null
            ? `Δ ${delta > 0 ? "+" : ""}${fmt(delta)}`
            : "Vergleich nicht verfügbar"}
        </small>
      </>
    );
  };
  async function save(candidateRows) {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const payload = (Array.isArray(candidateRows) ? candidateRows : rows).map(
        ({ rank, total_goals, custom_name, ...row }) => row
      );
      const response = await fetchViaPost(
        "put-team-strengths",
        JSON.stringify(payload)
      );
      if (!response.ok)
        throw new Error(
          "Speichern fehlgeschlagen. Die Änderungen bleiben im Editor."
        );
      setDirty(false);
      setRevision((value) => value + 1);
      setNotice("Team Settings gespeichert.");
      await onSaved?.();
      return true;
    } catch (error) {
      setError(error.message);
      return false;
    } finally {
      setSaving(false);
    }
  }
  const completeSettings = teams.filter((team) =>
    [
      "total_strength",
      "for_total",
      "against_total",
      "for_homefactor",
      "against_homefactor",
      "draw_factor",
    ].every((key) => settingNumber(team[key]) !== null)
  ).length;
  const savedAt = comparison?.record?.updated_at;
  return (
    <Box className="league-workarea">
      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}
      <TeamStrengthEditor
        league={league}
        onAnalysisSaved={()=>onSaved?.()}
        user_id={user?.id}
        season_id={season.id}
        teams={editorTeams}
        rows={rows}
        setRows={setRows}
        apiRef={apiRef}
        setHasChanges={setDirty}
        setCloseParent={() => onSaved?.()}
        seasonParamsReloadKey={revision}
        workspaceActions={
          <Button
            variant="contained"
            disabled={!dirty || saving}
            onClick={save}
          >
            Änderungen speichern
          </Button>
        }
        renderWorkspace={({
          seasonParams,
          onAdjust,
          onNormalize,
          onDetails,
          normalization,
          editor,
        }) => {
          const parameters = { ...data?.season_parameters, ...seasonParams };
          if (renderWorkspaceContent) return renderWorkspaceContent({ parameters, rows, savedRows: sortAndRankTeams(editorTeams), dirty, saving, save,
            onPreview: (nextRows, changed = true) => { setRows(sortAndRankTeams(nextRows)); setDirty(changed); },
            reset: () => { setRows(sortAndRankTeams(editorTeams)); setDirty(false); },
            metrics, sources, comparisons, previousSummary, previousSeason, previousError,
            onAdjust, onNormalize, onDetails, normalization, editor, teamLogos });

          const definitions = [
            {
              key: "goal_average",
              title: "Torschnitt",
              field: "goalAverage",
              model: parameters.stored_goal_average,
            },
            {
              key: "home_advantage",
              title: "Heimvorteil",
              field: "homeAdvantage",
              model: parameters.exp_home_factor,
            },
            {
              key: "draw_factor",
              title: "Remis-Faktor",
              field: "drawRate",
              model: parameters.drawfactor,
            },
          ];
          const teamDifferences = rows.filter((team) => {
            const value = comparisons.should.get(Number(team.team_id))
              ?.fixtureSettingValues?.total_strength;
            return (
              settingNumber(value) !== null &&
              Number(value) > 0 &&
              Math.abs(Number(team.total_strength) / value - 1) >= 0.1
            );
          }).length;
          return (
            <>
              <Box className="league-observations">
                <Typography component="h2" variant="h6">
                  Was fällt auf?
                </Typography>
                <Typography>
                  {parameterObservation(
                    "Torschnitt",
                    parameters.stored_goal_average,
                    actual?.goalAverage,
                    expected?.goalAverage,
                    should?.goalAverage,
                    actual?.fixtures || 0
                  )}
                </Typography>
                <Typography>
                  {parameterObservation(
                    "Heimvorteil",
                    parameters.exp_home_factor,
                    actual?.homeAdvantage,
                    expected?.homeAdvantage,
                    should?.homeAdvantage,
                    actual?.fixtures || 0
                  )}
                </Typography>
                {actual?.fixtures > 0 && expected?.fixtures > 0 && (
                  <Typography>
                    Die tatsächliche Remisquote ({pct(actual.drawRate)}) liegt{" "}
                    {actual.drawRate < expected.drawRate
                      ? "unter"
                      : actual.drawRate > expected.drawRate
                      ? "über"
                      : "bei"}{" "}
                    der Erwartung ({pct(expected.drawRate)}).
                  </Typography>
                )}
                {teamDifferences > 0 && (
                  <Typography>
                    {teamDifferences} Teams weichen bei Strength um mindestens
                    10 % von Should ab.
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  {(actual?.fixtures || 0) < teams.length * 5
                    ? "Frühe Saison / kleine Stichprobe: Einzelne Spiele prägen die Werte noch stark."
                    : "Die Werte beschreiben die bisher erfassten Spiele; sie sind keine automatische Änderungsempfehlung."}{" "}
                  Abweichende Spielzahlen der Quellen bitte mitbeachten.
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    onClick={() => {
                      setParametersOpen(true);
                      document
                        .getElementById("league-parameters")
                        ?.scrollIntoView({
                          block: "center",
                          behavior: "smooth",
                        });
                    }}
                  >
                    Liga-Parameter prüfen
                  </Button>
                  <Button
                    onClick={() => {
                      setTeamsOpen(true);
                      document
                        .getElementById("league-teams")
                        ?.scrollIntoView({
                          block: "start",
                          behavior: "smooth",
                        });
                    }}
                  >
                    Teams ansehen
                  </Button>
                </Stack>
              </Box>
              {dirty && (
                <Alert
                  severity="info"
                  action={
                    <Stack direction="row">
                      <Button
                        disabled={saving}
                        onClick={() => {
                          setRows(sortAndRankTeams(editorTeams));
                          setDirty(false);
                        }}
                      >
                        Verwerfen
                      </Button>
                      <Button disabled={saving} onClick={save}>
                        {saving ? "Speichert …" : "Speichern"}
                      </Button>
                    </Stack>
                  }
                >
                  Ungespeicherte Änderungen · keine automatische Normierung.
                  Ligawechsel erst nach Speichern oder Verwerfen.
                </Alert>
              )}
              <Box id="league-parameters">
                <LeagueSection
                  title="Liga-Parameter"
                  summary="Torschnitt · Heimvorteil · Remis-Faktor — einordnen und gezielt anpassen"
                  expanded={parametersOpen}
                  onChange={(_, open) => setParametersOpen(open)}
                >
                  <Box className="league-parameter-grid">
                    {definitions.map((item) => (
                      <Box className="league-parameter-card" key={item.key}>
                        <Typography component="h3" fontWeight={800}>
                          {item.title}
                        </Typography>
                        <div className="league-model-number">
                          <small>Gespeichertes Modell</small>
                          {fmt(item.model)}
                        </div>
                        {item.key === "draw_factor" && (
                          <Typography variant="body2">
                            Liga-Erwartung bisher:{" "}
                            <b>{pct(expected?.drawRate)}</b>
                          </Typography>
                        )}
                        <dl>
                          {[
                            ["Real", actual],
                            ["Expected", expected],
                            ["Should", should],
                          ].map(([label, row]) => (
                            <div key={label}>
                              <dt>
                                {label}
                                <small>
                                  {row?.fixtures > 0
                                    ? `${row.fixtures} Spiele`
                                    : "keine Spielbasis"}
                                </small>
                              </dt>
                              <dd>
                                {item.key === "draw_factor"
                                  ? pct(row?.[item.field])
                                  : fmt(row?.[item.field])}
                              </dd>
                            </div>
                          ))}
                        </dl>
                        <Typography variant="body2" color="text.secondary">
                          {item.key === "draw_factor"
                            ? "Der Faktor ist keine Wahrscheinlichkeit. Liegt die Summe der Torerwartungen unter dem doppelten Liga-Torschnitt, senkt ein höherer Faktor die Remiswahrscheinlichkeit. Oberhalb dieser Grenze kehrt sich die Wirkung im bestehenden Modell um."
                            : item.key === "home_advantage"
                            ? "1,000 bedeutet einen ausgeglichenen Toranteil. Die Anpassung verwendet die vorhandenen Heimprofile der Teams."
                            : "Expected beschreibt die bisherigen Prognosen, Should die aus Spielstatistiken abgeleitete Leistung."}
                        </Typography>
                        <Button
                          variant="outlined"
                          disabled={!rows.length || saving}
                          onClick={(event) => onAdjust(event, item.key)}
                        >
                          {item.title} anpassen
                        </Button>
                      </Box>
                    ))}
                  </Box>
                </LeagueSection>
              </Box>
              <Box id="league-teams">
                <LeagueSection
                  title="Teams & Spielstärken"
                  summary={`${teams.length} Teams · ${
                    completeSettings === teams.length && teams.length
                      ? "Settings vollständig"
                      : `${completeSettings} vollständige Settings`
                  }${dirty ? " · ungespeicherte Änderungen" : ""}`}
                  expanded={teamsOpen}
                  onChange={(_, open) => setTeamsOpen(open)}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    sx={{ mb: 1 }}
                  >
                    {[
                      ["Strength", strengthView, setStrengthView],
                      ["Goal Sum", goalsView, setGoalsView],
                    ].map(([label, value, setter]) => (
                      <TextField
                        key={label}
                        select
                        size="small"
                        sx={{ minWidth: 240 }}
                        label={`${label} · Vergleich mit`}
                        value={value}
                        onChange={(event) => setter(event.target.value)}
                      >
                        {options.map(([key, text]) => (
                          <MenuItem key={key} value={key}>
                            {text}
                          </MenuItem>
                        ))}
                      </TextField>
                    ))}
                    <Button onClick={() => onDetails()}>
                      Vollständiger Team-Editor
                    </Button>
                  </Stack>
                  {!marketRows.length && (
                    <Typography variant="body2" color="text.secondary">
                      Market Derived nicht verfügbar · Details unter Daten &
                      Diagnose.
                    </Typography>
                  )}
                  {previousError && (
                    <Alert severity="warning">{previousError}</Alert>
                  )}
                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small" className="league-strength-table">
                      <TableHead>
                        <TableRow>
                          {[
                            "#",
                            "Team",
                            "Strength",
                            "Vergleich · Δ Setting",
                            "Goal Sum",
                            "Vergleich · Δ Setting",
                            "",
                          ].map((label, index) => (
                            <TableCell key={index}>{label}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((team, index) => (
                          <TableRow
                            key={team.team_id}
                            onDoubleClick={() => onDetails(team.team_id)}
                          >
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                              >
                                {teamLogos?.get(Number(team.team_id)) && (
                                  <img
                                    src={teamLogos.get(Number(team.team_id))}
                                    width="25"
                                    height="25"
                                    alt=""
                                  />
                                )}
                                <b>{team.custom_name || team.team_name}</b>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <b>{fmt(team.total_strength)}</b>
                            </TableCell>
                            <TableCell>
                              {comparisonCell(
                                team,
                                strengthView,
                                "total_strength"
                              )}
                            </TableCell>
                            <TableCell>
                              <b>{fmt(team.total_goals)}</b>
                            </TableCell>
                            <TableCell>
                              {comparisonCell(team, goalsView, "total_goals")}
                            </TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                onClick={() => onDetails(team.team_id)}
                              >
                                Details
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                  <Typography variant="caption">
                    Δ = Setting minus Vergleich. Torprofile werden wie bisher
                    aus Heim- und Auswärtswerten ermittelt; fehlt eine Seite,
                    bleibt der Vergleich leer.
                  </Typography>
                  <Box className="league-normalization">
                    <b>Normierung</b>
                    <Typography>
                      For {fmt(normalization.sumForTotal)} · Against{" "}
                      {fmt(normalization.sumAgainstTotal)} · Differenz{" "}
                      {fmt(normalization.totalDifference)} · gemeinsamer
                      Zielwert{" "}
                      {fmt(
                        (normalization.sumForTotal +
                          normalization.sumAgainstTotal) /
                          2
                      )}
                    </Typography>
                    <Typography variant="body2">
                      {normalization.goalTotalsAreNormalized
                        ? "Torwerte sind ausgeglichen."
                        : Math.abs(normalization.totalDifference) /
                            Math.max(
                              normalization.sumForTotal,
                              normalization.sumAgainstTotal,
                              1
                            ) <
                          0.01
                        ? "Kleine Abweichung unter 1 % – keine dringende Aktion."
                        : "Abweichung ab 1 % – Normierung prüfen."}{" "}
                      Eine Änderung einzelner Teams löst keine Normierung aus.
                    </Typography>
                    <Button
                      disabled={!rows.length}
                      onClick={() => onNormalize("goal_totals")}
                    >
                      Details / Tore normieren
                    </Button>
                    <Button
                      disabled={!rows.length}
                      onClick={() => onNormalize("home_advantage")}
                    >
                      Heimfaktoren prüfen
                    </Button>
                  </Box>
                  <Box component="details">
                    <summary>
                      Weitere Teamdiagnosen · Strength / Goals Sum / Home-Away /
                      Faktoren
                    </summary>
                    <CompactLeagueTable
                      actualRows={sources.actual}
                      expectedRows={sources.expected}
                      shouldRows={sources.should}
                      teamSettings={Object.fromEntries(
                        rows.map((row) => [row.team_id, row])
                      )}
                      teamLogos={teamLogos}
                      seasonId={season.id}
                    />
                  </Box>
                  {editor}
                </LeagueSection>
              </Box>
              <LeagueSection
                title="Liga-Analyse"
                summary="Perfektes Tippspiel · 1X2 · Wahrscheinlichkeiten · Tore · Abweichungen"
              >
                <Box sx={{ overflowX: "auto" }}>
                  <LeagueSeasonComparison data={data} />
                </Box>
              </LeagueSection>
              {indications}
              <LeagueSection
                title="Daten & Diagnose"
                summary="Datenbasis, Abdeckung und fehlende Vergleiche"
              >
                <Typography>
                  Liga {league.id} · Saison {season.name} ({season.id}) ·{" "}
                  {savedAt
                    ? `Analyse aktualisiert: ${new Date(savedAt).toLocaleString(
                        "de-DE"
                      )}`
                    : "Kein Analyse-Zeitstempel vorhanden"}
                </Typography>
                <Typography>
                  Market Derived:{" "}
                  {marketRows.length
                    ? `${marketRows.length} Teams mit gespeicherten Tabellenwerten`
                    : "nicht verfügbar – der geladene Saisonrecord enthält keine Market-Tabelle. Einzelne Fixture-Werte und die Saisonaggregation sind getrennte Bestände."}
                </Typography>
                <Typography variant="body2">
                  Quelle: USER_SEASON_STATS.market.table; Expected und Should
                  stammen aus demselben vorhandenen Saisonrecord. Eine fehlende
                  Markttabelle wird beim Öffnen nicht neu berechnet oder
                  gespeichert.
                </Typography>
                <Box
                  component="pre"
                  sx={{ whiteSpace: "pre-wrap", fontSize: ".78rem" }}
                >
                  {JSON.stringify(
                    {
                      expected: comparison?.record?.expected_table_summary,
                      should: comparison?.record?.should_table_summary,
                      market:
                        comparison?.record?.market_table_summary ||
                        comparison?.record?.market?.coverage,
                      marketOmissions: comparison?.record?.market?.omissions,
                    },
                    null,
                    2
                  )}
                </Box>
                {diagnosis}
              </LeagueSection>
            </>
          );
        }}
      />
    </Box>
  );
}
